import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical, Link as LinkIcon, Copy } from "lucide-react";

const STAGES = [
  { v: "neu", label: "Neu", color: "border-slate-500 text-slate-300", dot: "bg-slate-400" },
  { v: "angebot", label: "Angebot", color: "border-amber-500/40 text-amber-300", dot: "bg-amber-400" },
  { v: "verhandlung", label: "Verhandlung", color: "border-violet-500/40 text-violet-300", dot: "bg-violet-400" },
  { v: "gewonnen", label: "Gewonnen", color: "border-emerald-500/40 text-emerald-300", dot: "bg-emerald-400" },
  { v: "verloren", label: "Verloren", color: "border-red-500/40 text-red-300", dot: "bg-red-400" },
];

const EUR = (v, c = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(v || 0);

const emptyForm = {
  title: "", lead_id: "", product_ids: [], value: 0, currency: "EUR",
  stage: "neu", expected_close_date: "", notes: "",
};

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l, p] = await Promise.all([
        api.get("/deals"), api.get("/leads"), api.get("/products"),
      ]);
      setDeals(d.data); setLeads(l.data); setProducts(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.v, []]));
    for (const d of deals) {
      if (map[d.stage]) map[d.stage].push(d);
    }
    return map;
  }, [deals]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      ...d,
      lead_id: d.lead_id || "",
      product_ids: d.product_ids || [],
      expected_close_date: d.expected_close_date ? d.expected_close_date.slice(0, 10) : "",
    });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      value: Number(form.value) || 0,
      lead_id: form.lead_id || null,
      expected_close_date: form.expected_close_date || null,
    };
    try {
      if (editing) {
        await api.put(`/deals/${editing.id}`, payload);
        toast.success("Deal aktualisiert");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal erstellt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/deals/${confirmDel.id}`);
      toast.success("Deal gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moveDeal = async (dealId, newStage) => {
    const deal = deals.find((d) => d.id === dealId);
    if (!deal || deal.stage === newStage) return;
    // optimistic
    setDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, stage: newStage } : d));
    try {
      await api.patch(`/deals/${dealId}/stage`, { stage: newStage });
      toast.success(`In „${STAGES.find((s) => s.v === newStage)?.label}" verschoben`);
    } catch (err) {
      toast.error("Fehler beim Verschieben");
      load();
    }
  };

  const totalByStage = (stage) => byStage[stage].reduce((a, d) => a + (d.value || 0), 0);
  const leadName = (id) => leads.find((l) => l.id === id)?.name || "";

  const createOfferLink = async (deal) => {
    try {
      const { data } = await api.post(`/deals/${deal.id}/offer-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Angebots-Link kopiert", { description: data.url });
      } catch {
        toast.success("Angebots-Link erstellt", { description: data.url });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Erzeugen des Links");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="pipeline-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Vertrieb</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Pipeline</h1>
          <p className="text-sm text-slate-400 mt-2">{deals.length} Deals · Ziehen zum Verschieben zwischen den Stufen.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-deal-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neuer Deal
        </Button>
      </header>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Lade…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 items-start">
          {STAGES.map((s) => (
            <div
              key={s.v}
              data-testid={`kanban-column-${s.v}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(s.v); }}
              onDragLeave={() => setDragOverStage((cur) => cur === s.v ? null : cur)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                if (draggedId) moveDeal(draggedId, s.v);
                setDraggedId(null);
              }}
              className={`rounded-xl border bg-[#111827] p-3 min-h-[400px] transition
                ${dragOverStage === s.v ? "border-emerald-500/60 bg-emerald-500/[0.04]" : "border-white/5"}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                  <span className="font-semibold text-white text-sm">{s.label}</span>
                  <Badge variant="outline" className="border-white/10 text-slate-400 text-[10px]">
                    {byStage[s.v].length}
                  </Badge>
                </div>
                <span className="text-xs text-slate-500 tabular-nums" data-testid={`kanban-total-${s.v}`}>
                  {EUR(totalByStage(s.v))}
                </span>
              </div>

              <div className="space-y-2">
                {byStage[s.v].length === 0 && (
                  <div className="text-xs text-slate-600 text-center py-6 border border-dashed border-white/5 rounded-lg">
                    Keine Deals
                  </div>
                )}
                {byStage[s.v].map((d) => (
                  <div
                    key={d.id}
                    data-testid={`deal-card-${d.id}`}
                    draggable
                    onDragStart={() => setDraggedId(d.id)}
                    onDragEnd={() => { setDraggedId(null); setDragOverStage(null); }}
                    className={`group relative rounded-lg border border-white/5 bg-[#0b0f17] p-3 cursor-grab active:cursor-grabbing
                      hover:border-emerald-500/40 transition ${draggedId === d.id ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1 min-w-0">
                        <GripVertical className="h-3.5 w-3.5 text-slate-600 shrink-0" />
                        <div className="font-medium text-white text-sm truncate">{d.title}</div>
                      </div>
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-emerald-300"
                          onClick={() => createOfferLink(d)} data-testid={`deal-offer-link-${d.id}`} title="Angebots-Link kopieren">
                          <LinkIcon className="h-3 w-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-white"
                          onClick={() => openEdit(d)} data-testid={`deal-edit-${d.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400 hover:text-red-300"
                          onClick={() => setConfirmDel(d)} data-testid={`deal-delete-${d.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mb-2 truncate">{leadName(d.lead_id) || "—"}</div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-emerald-400 font-semibold tabular-nums">{EUR(d.value, d.currency)}</span>
                      {d.expected_close_date ? (
                        <span className="text-slate-500">{d.expected_close_date.slice(0, 10)}</span>
                      ) : null}
                    </div>
                    {/* Mobile fallback: stage change via select */}
                    <div className="md:hidden mt-2">
                      <Select value={d.stage} onValueChange={(v) => moveDeal(d.id, v)}>
                        <SelectTrigger data-testid={`deal-stage-select-${d.id}`} className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.map((st) => <SelectItem key={st.v} value={st.v}>{st.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl" data-testid="deal-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Deal bearbeiten" : "Neuer Deal"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Essentials */}
            <Field label="Titel" required>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                required data-testid="deal-form-title" placeholder="z. B. Angebot für Firma Müller" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Lead">
                <Select value={form.lead_id || "__none__"} onValueChange={(v) => setForm({ ...form, lead_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="deal-form-lead"><SelectValue placeholder="Kein Lead" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Keiner —</SelectItem>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} {l.company ? `· ${l.company}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Stufe">
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="deal-form-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Wert (EUR)">
                <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="deal-form-value" />
              </Field>
              <Field label="Erwarteter Abschluss">
                <Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} data-testid="deal-form-date" />
              </Field>
            </div>

            {/* Erweitert */}
            <details className="group border-t border-white/5 pt-3">
              <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="deal-form-advanced-toggle">
                <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (Produkte, Notizen)
              </summary>
              <div className="space-y-4 mt-3">
                <Field label="Produkte">
                  <div className="flex flex-wrap gap-2 p-2 rounded-md border border-white/10 bg-white/5 max-h-32 overflow-auto" data-testid="deal-form-products">
                    {products.map((p) => {
                      const on = form.product_ids.includes(p.id);
                      return (
                        <button type="button" key={p.id}
                          onClick={() => setForm({ ...form, product_ids: on ? form.product_ids.filter((x) => x !== p.id) : [...form.product_ids, p.id] })}
                          className={`text-xs px-2 py-1 rounded border transition
                            ${on ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                                 : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
                          {p.name}
                        </button>
                      );
                    })}
                    {products.length === 0 && <div className="text-xs text-slate-500">Keine Produkte vorhanden.</div>}
                  </div>
                </Field>
                <Field label="Notizen">
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="deal-form-notes" />
                </Field>
              </div>
            </details>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="deal-form-cancel">Abbrechen</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="deal-form-submit">
                {editing ? "Änderungen speichern" : "Deal anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Deal löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              „{confirmDel?.title}" wird dauerhaft entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deal-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="deal-delete-confirm" className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-300 text-xs mb-1 block">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}
